import {
  CreateButton,
  DateField,
  List,
  SaveButton,
  ShowButton,
  TextField,
  useSelect,
  useTable,
} from "@refinedev/antd";

import { Card, Col, Divider, Form, FormProps, Input, Row, Select, Space, Table, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";

import { CrudFilters, IResourceComponentsProps, getDefaultFilter } from "@refinedev/core";

import { IShipco, IVesselTransfer } from "interfaces";
import React from "react";


const TRANSFER_TYPE_LABELS: Record<string, string> = {
  to_old_vessels: "Deactivate",
  between_shipcos: "Transfer",
  from_old_vessels: "Reactivate",
};

const TRANSFER_TYPE_COLORS: Record<string, string> = {
  to_old_vessels: "volcano",
  between_shipcos: "geekblue",
  from_old_vessels: "cyan",
};

export const VesselTransferList: React.FC<IResourceComponentsProps> = () => {
  const {
    tableProps,
    tableQuery: { isLoading },
    searchFormProps,
    filters,
  } = useTable<IVesselTransfer>({
    resource: "vessel-transfer",
    meta: {
      join: [
        { field: "sourceShipco" },
        { field: "targetShipco" },
        { field: "items" },
      ],
    },
    onSearch: (params: any) => {
      const filters: CrudFilters = [];
      const { transferType, sourceShipco, targetShipco, performedBy } = params;

      filters.push({
        field: "transferType",
        operator: "eq",
        value: transferType ? transferType : undefined,
      });
      filters.push({
        field: "sourceShipco.registrationName",
        operator: "eq",
        value: sourceShipco ? sourceShipco : undefined,
      });
      filters.push({
        field: "targetShipco.registrationName",
        operator: "eq",
        value: targetShipco ? targetShipco : undefined,
      });
      filters.push({
        field: "performedBy",
        operator: "contains",
        value: performedBy ? performedBy : undefined,
      });

      return filters;
    },
    sorters: {
      initial: [{ field: "id", order: "desc" }],
    },
    pagination: {
      pageSize: 20,
      mode: "server",
    },
    filters: {
      defaultBehavior: "replace",
    },
  });

  const columns: ColumnsType<IVesselTransfer> = [
    {
      title: "Transfer Type",
      dataIndex: "transferType",
      key: "transferType",
      width: 140,
      render: (value: string) => (
        <Tag color={TRANSFER_TYPE_COLORS[value] || "blue"}>{TRANSFER_TYPE_LABELS[value] || value}</Tag>
      ),
    },
    {
      title: "Source Shipco",
      key: "sourceShipco",
      width: 200,
      render: (_, record) => (
        <TextField
          value={record.sourceShipco?.registrationName || "N/A"}
        />
      ),
    },
    {
      title: "Target Shipco",
      key: "targetShipco",
      width: 200,
      render: (_, record) => (
        <TextField
          value={record.targetShipco?.registrationName || "N/A"}
        />
      ),
    },
    {
      title: "IMO Number",
      key: "imoNumber",
      width: 120,
      render: (_, record) => (
        <TextField value={record.items?.[0]?.imoNumber || "N/A"} />
      ),
    },
    {
      title: "Performed By",
      dataIndex: "performedBy",
      key: "performedBy",
      width: 150,
      render: (value) => <TextField value={value || "N/A"} />,
    },
    {
      title: "Date",
      dataIndex: "effectiveDate",
      key: "effectiveDate",
      width: 160,
      render: (value) =>
        value ? (
          <DateField value={value} format="DD-MMM-YYYY" />
        ) : (
          <TextField value="N/A" />
        ),
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 80,
      render: (_, record) => (
        <Space>
          <ShowButton hideText size="small" recordItemId={record.id} />
        </Space>
      ),
    },
  ];

  return (
    <List
      breadcrumb={false}
      headerProps={{
        title: "Vessel Transfers",
        subTitle: "transfer history and management",
        tags: isLoading ? (
          <Tag color="orange">loading</Tag>
        ) : (
          <Tag color="green">live</Tag>
        ),
        extra: (
          <Space>
            <CreateButton>New Transfer</CreateButton>
          </Space>
        ),
        ghost: true,
        footer: (
          <Divider plain>
            © {new Date().getFullYear()} e-Mariner NavSolutions
          </Divider>
        ),
      }}
    >
      <Row gutter={[16, 16]}>
        <Col xl={24} lg={24} xs={24}>
          <Card type="inner" title={"Filter"}>
            <Filter formProps={searchFormProps} filters={filters || []} />
          </Card>
        </Col>
        <Col xl={24} xs={24}>
          <Table
            {...tableProps}
            columns={columns}
            rowKey="id"
            size="middle"
            scroll={{ x: 1200 }}
            sticky
            pagination={{
              ...tableProps.pagination,
              showSizeChanger: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total}`,
            }}
          />
        </Col>
      </Row>
    </List>
  );
};

const Filter: React.FC<{ formProps: FormProps; filters: CrudFilters }> = (
  props
) => {
  const { formProps, filters } = props;

  const { selectProps: sourceShipcoSelectProps } = useSelect<IShipco>({
    resource: "shipco",
    optionLabel: "registrationName",
    optionValue: "registrationName",
    pagination: { mode: "server" },
  });

  const { selectProps: targetShipcoSelectProps } = useSelect<IShipco>({
    resource: "shipco",
    optionLabel: "registrationName",
    optionValue: "registrationName",
    pagination: { mode: "server" },
  });

  return (
    <Form
      layout="vertical"
      {...formProps}
      initialValues={{
        transferType: getDefaultFilter("transferType", filters),
        sourceShipco: getDefaultFilter("sourceShipco.registrationName", filters),
        targetShipco: getDefaultFilter("targetShipco.registrationName", filters),
        performedBy: getDefaultFilter("performedBy", filters),
      }}
    >
      <Row gutter={[16, 16]} align="middle" justify="space-between">
        <Col xs={24} sm={24} md={5}>
          <Form.Item label={"Transfer Type"} name="transferType">
            <Select placeholder="filter by type" allowClear>
              <Select.Option value="to_old_vessels">Deactivate</Select.Option>
              <Select.Option value="between_shipcos">Transfer</Select.Option>
              <Select.Option value="from_old_vessels">Reactivate</Select.Option>
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={5}>
          <Form.Item label={"Source Shipco"} name="sourceShipco">
            <Select
              placeholder="filter by source shipco"
              allowClear
              showSearch
              {...sourceShipcoSelectProps}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={5}>
          <Form.Item label={"Target Shipco"} name="targetShipco">
            <Select
              placeholder="filter by target shipco"
              allowClear
              showSearch
              {...targetShipcoSelectProps}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={5}>
          <Form.Item label={"Performed By"} name="performedBy">
            <Input placeholder="filter by user" allowClear />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={4}>
          <Form.Item style={{ marginBottom: 0 }}>
            <SaveButton onClick={formProps.form?.submit} size="large" block>
              Filter
            </SaveButton>
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};
